import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

interface TourStep {
  targetSelector: string;
  title: string;
  description: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

const tourCopy = {
  en: {
    startTour: 'Take a Tour',
    next: 'Next',
    prev: 'Back',
    done: 'Got it!',
    skip: 'Skip tour',
    stepOf: 'of',
    steps: [
      { title: 'Your Credits', description: 'See how many Living Announcements you have left to create. Each message you share grows your Living Tree and strengthens your connections.', targetSelector: '[data-tour="credits"]', position: 'bottom' as const },
      { title: 'Write Something', description: 'Tap here to create your first Living Announcement — a heartfelt message for someone who matters.', targetSelector: '[data-tour="create-btn"]', position: 'bottom' as const },
      { title: 'Badges & Rewards', description: 'Earn badges for milestones like your first message, connection streaks, and relationship depth. Climb tiers to unlock rewards and discounts!', targetSelector: '[data-tour="engagement"]', position: 'bottom' as const },
      { title: 'Your Moments', description: 'Tap a fruit on your Living Tree to view the message you sent. You can share or publish it anytime.', targetSelector: '[data-tour="announcements"]', position: 'top' as const },
      { title: 'Your Profile', description: 'From here you can access Settings, add Credits, visit the Gift Store, or Log Out.', targetSelector: '[data-tour="profile-menu"]', position: 'bottom' as const },
      { title: 'Quiet Drafts', description: 'Unfinished or saved messages live here. Come back anytime to continue writing where you left off.', targetSelector: '[data-tour="quiet-drafts"]', position: 'top' as const },
    ],
  },
  es: {
    startTour: 'Hacer un Tour',
    next: 'Siguiente',
    prev: 'Atrás',
    done: '¡Entendido!',
    skip: 'Saltar tour',
    stepOf: 'de',
    steps: [
      { title: 'Tus Créditos', description: 'Mira cuántos anuncios te quedan por crear. Cada mensaje que compartes hace crecer tu Árbol Viviente y fortalece tus conexiones.', targetSelector: '[data-tour="credits"]', position: 'bottom' as const },
      { title: 'Escribe Algo', description: 'Toca aquí para crear tu primer anuncio — un mensaje sincero para alguien importante.', targetSelector: '[data-tour="create-btn"]', position: 'bottom' as const },
      { title: 'Insignias y Recompensas', description: 'Gana insignias por hitos como tu primer mensaje, rachas de conexión y profundidad de relaciones. ¡Sube de nivel para desbloquear recompensas!', targetSelector: '[data-tour="engagement"]', position: 'bottom' as const },
      { title: 'Tus Momentos', description: 'Toca una fruta en tu Árbol Viviente para ver el mensaje que enviaste. Puedes compartirlo o publicarlo.', targetSelector: '[data-tour="announcements"]', position: 'top' as const },
      { title: 'Tu Perfil', description: 'Desde aquí puedes acceder a Configuración, agregar Créditos, visitar la Tienda de Regalos o Cerrar Sesión.', targetSelector: '[data-tour="profile-menu"]', position: 'bottom' as const },
      { title: 'Borradores Guardados', description: 'Los mensajes sin terminar o guardados están aquí. Vuelve cuando quieras para seguir escribiendo.', targetSelector: '[data-tour="quiet-drafts"]', position: 'top' as const },
    ],
  },
  fr: {
    startTour: 'Faire le Tour',
    next: 'Suivant',
    prev: 'Retour',
    done: 'Compris !',
    skip: 'Passer',
    stepOf: 'de',
    steps: [
      { title: 'Vos Crédits', description: 'Voyez combien d\'annonces vous pouvez encore créer. Chaque message partagé fait grandir votre Arbre Vivant et renforce vos liens.', targetSelector: '[data-tour="credits"]', position: 'bottom' as const },
      { title: 'Écrire Quelque Chose', description: 'Appuyez ici pour créer votre première annonce — un message sincère pour quelqu\'un qui compte.', targetSelector: '[data-tour="create-btn"]', position: 'bottom' as const },
      { title: 'Badges et Récompenses', description: 'Gagnez des badges pour vos jalons : premier message, séries de connexions, profondeur relationnelle. Montez de niveau pour débloquer des récompenses !', targetSelector: '[data-tour="engagement"]', position: 'bottom' as const },
      { title: 'Vos Moments', description: 'Touchez un fruit sur votre Arbre Vivant pour voir le message envoyé. Vous pouvez le partager ou le publier.', targetSelector: '[data-tour="announcements"]', position: 'top' as const },
      { title: 'Votre Profil', description: 'Accédez aux Paramètres, ajoutez des Crédits, visitez la Boutique ou Déconnectez-vous.', targetSelector: '[data-tour="profile-menu"]', position: 'bottom' as const },
      { title: 'Brouillons', description: 'Les messages non terminés ou sauvegardés se trouvent ici. Revenez quand vous voulez pour continuer.', targetSelector: '[data-tour="quiet-drafts"]', position: 'top' as const },
    ],
  },
  pt: {
    startTour: 'Fazer um Tour',
    next: 'Próximo',
    prev: 'Voltar',
    done: 'Entendi!',
    skip: 'Pular tour',
    stepOf: 'de',
    steps: [
      { title: 'Seus Créditos', description: 'Veja quantos anúncios você ainda pode criar. Cada mensagem compartilhada faz sua Árvore Viva crescer e fortalece seus laços.', targetSelector: '[data-tour="credits"]', position: 'bottom' as const },
      { title: 'Escreva Algo', description: 'Toque aqui para criar seu primeiro anúncio — uma mensagem sincera para alguém especial.', targetSelector: '[data-tour="create-btn"]', position: 'bottom' as const },
      { title: 'Medalhas e Recompensas', description: 'Ganhe medalhas por marcos como sua primeira mensagem, sequências de conexão e profundidade de relacionamentos. Suba de nível para desbloquear recompensas!', targetSelector: '[data-tour="engagement"]', position: 'bottom' as const },
      { title: 'Seus Momentos', description: 'Toque uma fruta na sua Árvore Viva para ver a mensagem enviada. Você pode compartilhá-la ou publicá-la.', targetSelector: '[data-tour="announcements"]', position: 'top' as const },
      { title: 'Seu Perfil', description: 'Acesse Configurações, adicione Créditos, visite a Loja de Presentes ou Saia.', targetSelector: '[data-tour="profile-menu"]', position: 'bottom' as const },
      { title: 'Rascunhos', description: 'Mensagens não terminadas ou salvas ficam aqui. Volte quando quiser para continuar escrevendo.', targetSelector: '[data-tour="quiet-drafts"]', position: 'top' as const },
    ],
  },
  ar: {
    startTour: 'ابدأ الجولة',
    next: 'التالي',
    prev: 'السابق',
    done: 'فهمت!',
    skip: 'تخطي الجولة',
    stepOf: 'من',
    steps: [
      { title: 'رصيدك', description: 'شاهد عدد الإعلانات المتبقية. كل رسالة تشاركها تُنمّي شجرتك الحية وتقوّي روابطك.', targetSelector: '[data-tour="credits"]', position: 'bottom' as const },
      { title: 'اكتب شيئاً', description: 'انقر هنا لإنشاء أول إعلان لك — رسالة صادقة لشخص مهم.', targetSelector: '[data-tour="create-btn"]', position: 'bottom' as const },
      { title: 'الشارات والمكافآت', description: 'اكسب شارات لإنجازات مثل أول رسالة وسلسلة التواصل وعمق العلاقات. ارتقِ بمستواك لفتح المكافآت!', targetSelector: '[data-tour="engagement"]', position: 'bottom' as const },
      { title: 'لحظاتك', description: 'انقر على ثمرة في شجرتك الحية لعرض الرسالة التي أرسلتها. يمكنك مشاركتها أو نشرها.', targetSelector: '[data-tour="announcements"]', position: 'top' as const },
      { title: 'ملفك الشخصي', description: 'من هنا يمكنك الوصول إلى الإعدادات وإضافة الرصيد وزيارة متجر الهدايا أو تسجيل الخروج.', targetSelector: '[data-tour="profile-menu"]', position: 'bottom' as const },
      { title: 'المسودات', description: 'الرسائل غير المكتملة أو المحفوظة موجودة هنا. عد في أي وقت لمتابعة الكتابة.', targetSelector: '[data-tour="quiet-drafts"]', position: 'top' as const },
    ],
  },
  ja: {
    startTour: 'ツアーを開始',
    next: '次へ',
    prev: '戻る',
    done: '了解！',
    skip: 'スキップ',
    stepOf: '/',
    steps: [
      { title: 'クレジット', description: '作成できるアナウンスの残数を確認できます。メッセージを送るたびにリビングツリーが成長し、つながりが深まります。', targetSelector: '[data-tour="credits"]', position: 'bottom' as const },
      { title: '何か書く', description: 'ここをタップして最初のアナウンスを作成しましょう。大切な人への心のこもったメッセージです。', targetSelector: '[data-tour="create-btn"]', position: 'bottom' as const },
      { title: 'バッジと報酬', description: '初メッセージ、つながりの連続記録、関係の深さなどのマイルストーンでバッジを獲得。ティアを上げて報酬をアンロック！', targetSelector: '[data-tour="engagement"]', position: 'bottom' as const },
      { title: 'あなたのモーメント', description: 'リビングツリーの果実をタップして送ったメッセージを確認できます。共有や公開がいつでも可能です。', targetSelector: '[data-tour="announcements"]', position: 'top' as const },
      { title: 'プロフィール', description: 'ここから設定、クレジット追加、ギフトストア、ログアウトにアクセスできます。', targetSelector: '[data-tour="profile-menu"]', position: 'bottom' as const },
      { title: '下書き', description: '未完成または保存したメッセージはここにあります。いつでも戻って続きを書けます。', targetSelector: '[data-tour="quiet-drafts"]', position: 'top' as const },
    ],
  },
  ko: {
    startTour: '투어 시작',
    next: '다음',
    prev: '이전',
    done: '알겠어요!',
    skip: '건너뛰기',
    stepOf: '/',
    steps: [
      { title: '크레딧', description: '남은 공지 수를 확인하세요. 메시지를 공유할 때마다 리빙 트리가 자라고 연결이 강화됩니다.', targetSelector: '[data-tour="credits"]', position: 'bottom' as const },
      { title: '무언가 쓰기', description: '여기를 탭하여 첫 공지를 만드세요 — 소중한 사람에게 보내는 진심 어린 메시지입니다.', targetSelector: '[data-tour="create-btn"]', position: 'bottom' as const },
      { title: '배지와 보상', description: '첫 메시지, 연결 연속 기록, 관계 깊이 등 이정표마다 배지를 획득하세요. 티어를 올려 보상을 잠금 해제!', targetSelector: '[data-tour="engagement"]', position: 'bottom' as const },
      { title: '당신의 순간들', description: '리빙 트리의 열매를 탭하여 보낸 메시지를 확인하세요. 공유하거나 게시할 수 있습니다.', targetSelector: '[data-tour="announcements"]', position: 'top' as const },
      { title: '프로필', description: '여기서 설정, 크레딧 추가, 기프트 스토어, 로그아웃에 접근할 수 있습니다.', targetSelector: '[data-tour="profile-menu"]', position: 'bottom' as const },
      { title: '임시 저장', description: '미완성이거나 저장된 메시지가 여기에 있습니다. 언제든 돌아와서 이어 쓸 수 있습니다.', targetSelector: '[data-tour="quiet-drafts"]', position: 'top' as const },
    ],
  },
  id: {
    startTour: 'Mulai Tur',
    next: 'Berikutnya',
    prev: 'Kembali',
    done: 'Mengerti!',
    skip: 'Lewati tur',
    stepOf: 'dari',
    steps: [
      { title: 'Kredit Anda', description: 'Lihat berapa pengumuman yang bisa Anda buat. Setiap pesan yang dibagikan menumbuhkan Pohon Hidup dan memperkuat koneksi Anda.', targetSelector: '[data-tour="credits"]', position: 'bottom' as const },
      { title: 'Tulis Sesuatu', description: 'Ketuk di sini untuk membuat pengumuman pertama — pesan tulus untuk seseorang yang berarti.', targetSelector: '[data-tour="create-btn"]', position: 'bottom' as const },
      { title: 'Lencana dan Hadiah', description: 'Dapatkan lencana untuk pencapaian seperti pesan pertama, rangkaian koneksi, dan kedalaman hubungan. Naik tingkat untuk membuka hadiah!', targetSelector: '[data-tour="engagement"]', position: 'bottom' as const },
      { title: 'Momen Anda', description: 'Ketuk buah di Pohon Hidup untuk melihat pesan yang Anda kirim. Anda bisa membagikan atau menerbitkannya.', targetSelector: '[data-tour="announcements"]', position: 'top' as const },
      { title: 'Profil Anda', description: 'Dari sini Anda bisa mengakses Pengaturan, menambah Kredit, mengunjungi Toko Hadiah, atau Keluar.', targetSelector: '[data-tour="profile-menu"]', position: 'bottom' as const },
      { title: 'Draf Tersimpan', description: 'Pesan yang belum selesai atau tersimpan ada di sini. Kembali kapan saja untuk melanjutkan menulis.', targetSelector: '[data-tour="quiet-drafts"]', position: 'top' as const },
    ],
  },
  hi: {
    startTour: 'टूर लें',
    next: 'अगला',
    prev: 'पीछे',
    done: 'समझ गया!',
    skip: 'टूर छोड़ें',
    stepOf: 'में से',
    steps: [
      { title: 'आपके क्रेडिट', description: 'देखें कि आपके पास कितनी घोषणाएं बनाने के लिए बची हैं। हर संदेश आपके जीवित पेड़ को बढ़ाता है।', targetSelector: '[data-tour="credits"]', position: 'bottom' as const },
      { title: 'कुछ लिखें', description: 'अपनी पहली घोषणा बनाने के लिए यहां टैप करें — किसी ख़ास के लिए दिल से संदेश।', targetSelector: '[data-tour="create-btn"]', position: 'bottom' as const },
      { title: 'बैज और पुरस्कार', description: 'पहले संदेश, कनेक्शन स्ट्रीक और रिश्तों की गहराई जैसे मील के पत्थर पर बैज कमाएं!', targetSelector: '[data-tour="engagement"]', position: 'bottom' as const },
      { title: 'आपके पल', description: 'भेजे गए संदेश देखने के लिए पेड़ पर फल टैप करें। आप कभी भी साझा कर सकते हैं।', targetSelector: '[data-tour="announcements"]', position: 'top' as const },
      { title: 'आपकी प्रोफ़ाइल', description: 'यहां से सेटिंग्स, क्रेडिट जोड़ें, गिफ्ट स्टोर या लॉग आउट करें।', targetSelector: '[data-tour="profile-menu"]', position: 'bottom' as const },
      { title: 'शांत ड्राफ्ट', description: 'अधूरे या सहेजे गए संदेश यहां हैं। कभी भी वापस आकर लिखना जारी रखें।', targetSelector: '[data-tour="quiet-drafts"]', position: 'top' as const },
    ],
  },
};

interface TooltipPosition {
  top: number;
  left: number;
  arrowPosition: 'top' | 'bottom' | 'left' | 'right';
}

function getTooltipPosition(
  targetRect: DOMRect,
  position: TourStep['position'],
): TooltipPosition {
  const gap = 16;
  const tooltipWidth = Math.min(320, window.innerWidth - 32);
  const tooltipHeight = 220;
  const padding = 16;

  const centerX = Math.max(padding, Math.min(targetRect.left + targetRect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - padding));

  let top: number;
  let arrowPosition: TooltipPosition['arrowPosition'];

  if (position === 'bottom' && targetRect.bottom + gap + tooltipHeight <= window.innerHeight - padding) {
    top = targetRect.bottom + gap;
    arrowPosition = 'top';
  } else if (position === 'top' && targetRect.top - gap - tooltipHeight >= padding) {
    top = targetRect.top - tooltipHeight - gap;
    arrowPosition = 'bottom';
  } else if (targetRect.top > window.innerHeight / 2) {
    top = Math.max(padding, targetRect.top - tooltipHeight - gap);
    arrowPosition = 'bottom';
  } else {
    top = Math.min(window.innerHeight - tooltipHeight - padding, targetRect.bottom + gap);
    arrowPosition = 'top';
  }

  return { top, left: centerX, arrowPosition };
}

interface GuidedTourProps {
  isActive: boolean;
  onEnd: () => void;
}

export function GuidedTour({ isActive, onEnd }: GuidedTourProps) {
  const { language } = useLanguage();
  const tc = lc(tourCopy, language);
  const steps = tc.steps as TourStep[];
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [ready, setReady] = useState(false);

  const isProfileStep = steps[currentStep]?.targetSelector === '[data-tour="profile-menu"]';

  // Unified position updater that works for all steps including profile
  const updatePosition = useCallback(() => {
    if (!isActive || currentStep >= steps.length) return;
    const step = steps[currentStep];
    const el = document.querySelector(step.targetSelector);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
      setTooltipPos(getTooltipPosition(rect, step.position));
    }
  }, [isActive, currentStep, steps]);

  // Handle profile dropdown open/close via custom events
  useEffect(() => {
    if (!isActive) return;
    setReady(false);

    if (isProfileStep) {
      window.dispatchEvent(new CustomEvent('tour:open-profile'));

      let attempts = 0;
      const poll = setInterval(() => {
        attempts++;
        const menuContent = document.querySelector('[data-tour="profile-menu"]');
        if (menuContent || attempts > 30) {
          clearInterval(poll);
          if (menuContent) {
            const rect = menuContent.getBoundingClientRect();
            setTargetRect(rect);
            setTooltipPos(getTooltipPosition(rect, 'bottom'));
          }
          setReady(true);
        }
      }, 60);

      return () => clearInterval(poll);
    } else {
      window.dispatchEvent(new CustomEvent('tour:close-profile'));
      const timer = setTimeout(() => setReady(true), 50);
      return () => clearTimeout(timer);
    }
  }, [isActive, currentStep, isProfileStep]);

  // Resize / scroll listeners — active for ALL steps once ready
  useEffect(() => {
    if (!isActive || !ready) return;

    // Initial position (with scroll for non-profile steps)
    const initTimer = setTimeout(() => {
      updatePosition();
      if (!isProfileStep) {
        const el = document.querySelector(steps[currentStep]?.targetSelector);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    const onResize = () => updatePosition();
    const onScroll = () => updatePosition();

    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      clearTimeout(initTimer);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [isActive, currentStep, updatePosition, ready, isProfileStep, steps]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      handleEnd();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  const handleEnd = () => {
    window.dispatchEvent(new CustomEvent('tour:close-profile'));
    setCurrentStep(0);
    onEnd();
  };

  if (!isActive || !tooltipPos || !targetRect || !ready) return null;

  const step = steps[currentStep];
  const isLast = currentStep === steps.length - 1;

  return (
    <>
      {/* Backdrop overlay with spotlight cutout — smooth SVG transition */}
      <div className="fixed inset-0 z-[9998]" onClick={handleEnd}>
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <mask id="tour-spotlight">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={targetRect.left - 6}
                y={targetRect.top - 6}
                width={targetRect.width + 12}
                height={targetRect.height + 12}
                rx="12"
                fill="black"
                style={{ transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(0,0,0,0.6)"
            mask="url(#tour-spotlight)"
          />
        </svg>
      </div>

      {/* Spotlight ring — smooth CSS transition */}
      <div
        className="fixed z-[9999] pointer-events-none rounded-xl ring-2 ring-primary ring-offset-2 ring-offset-background"
        style={{
          top: targetRect.top - 6,
          left: targetRect.left - 6,
          width: targetRect.width + 12,
          height: targetRect.height + 12,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />

      {/* Tooltip — smooth framer-motion transition */}
      <motion.div
        key={currentStep}
        initial={{ opacity: 0, y: 10, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        className="fixed z-[10000] w-[calc(100vw-2rem)] max-w-[320px] bg-card border border-border rounded-2xl shadow-2xl p-5"
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
          transition: 'top 0.4s cubic-bezier(0.4,0,0.2,1), left 0.4s cubic-bezier(0.4,0,0.2,1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleEnd}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Step counter */}
        <p className="text-xs text-muted-foreground mb-2">
          {currentStep + 1} {tc.stepOf} {steps.length}
        </p>

        <h3 className="text-base font-heading font-bold text-foreground mb-1.5">
          {step.title}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
          {step.description}
        </p>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleEnd}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {tc.skip}
          </button>
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button variant="ghost" size="sm" onClick={handlePrev} className="gap-1 h-8 transition-all duration-200">
                <ArrowLeft className="h-3 w-3" />
                {tc.prev}
              </Button>
            )}
            <Button size="sm" onClick={handleNext} className="gap-1 h-8 transition-all duration-200">
              {isLast ? tc.done : tc.next}
              {!isLast && <ArrowRight className="h-3 w-3" />}
            </Button>
          </div>
        </div>
      </motion.div>
    </>
  );
}

export function TourTriggerButton({ onClick }: { onClick: () => void }) {
  const { language } = useLanguage();
  const tc = lc(tourCopy, language);

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      className="gap-2 rounded-2xl glass border-border/50"
    >
      <MapPin className="h-4 w-4" />
      {tc.startTour}
    </Button>
  );
}
